import { useEffect, useMemo, useState } from 'react'
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSurveyById, saveQuestion, setSurveyAsFinish } from '../api/surveyAPI';
import { AnswerTypeEnum, SurveyDto, SurveyFormulaQuestionResponseDto, SurveyQuestionAnswerResponseDto, SurveyQuestionDto } from '../types';
import './SurveyForm.css';
import SurveyQuestionForm from '../components/SurveyQuestionForm';
import { getLocalizedText } from '../../../utils/TranslateDescriptionHelper';
import { getErrorMessage } from '../../../utils/ErrorMessageHelper';

export default function SurveyForm() {
    const { surveyId } = useParams();
    const { t } = useTranslation();
    const [survey, setSurvey] = useState<SurveyDto>();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(1);
    const [responseError, setResponseError] = useState<string>();
    const [pageError, setPageError] = useState<string>();
    const [isCompleted, setIsCompleted] = useState(false);
    const [startQuestionDate, setStartQuestionDate] = useState<Date>(new Date());

    useEffect(() => {
        setResponseError(undefined);
        setPageError(undefined);
        if (!surveyId) return;

        const loadSurvey = async () => {
            try {
                const data = await getSurveyById(surveyId);
                setSurvey(data);
                setCurrentQuestionIndex(data.lastQuestionAnsweredOrder - 1);
            } catch (err) {
                setCurrentQuestionIndex(-1);
                setPageError(getErrorMessage(err));
            }
        };

        loadSurvey();
    }, [])

    const orderedQuestions = useMemo(() => {
        const questions = survey?.questions.filter(question => question.answerType != AnswerTypeEnum.Formula || question.showFormulaInSurvey == true) ?? [];
        return [...questions].sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER));
    }, [survey]);

    const currentQuestion: SurveyQuestionDto | undefined = orderedQuestions[currentQuestionIndex];

    useEffect(() => {
        setResponseError(undefined);
        setPageError(undefined);
    }, [currentQuestionIndex]);

    const goToPreviousQuestion = () => {
        setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
    };

    const saveQuestionAnswer = async () => {
        if (!currentQuestion) return;

        if (currentQuestion.answerType == AnswerTypeEnum.Formula) {
            goToNextQuestion();
            return;
        }

        
        const answerResponse: SurveyQuestionAnswerResponseDto = {
            surveyParticipantId: surveyId!,
            surveyQuestionId: currentQuestion.id,
            response: currentQuestion.response,
            isAnswerMandatory: currentQuestion.isAnswerMandatory == true,
            isMultiselector: currentQuestion.isMultiselector == true,
            isOptionQuestion: currentQuestion.answerType == AnswerTypeEnum.Option,
            startDate: startQuestionDate,
            endDate: new Date()
        }
        let isValid = true;
        if (currentQuestion.isAnswerMandatory || answerResponse.response) {
            if (!answerResponse.response) {
                setResponseError(getErrorMessage(t("common:form.requiredFieldError")));
                isValid = false;
                return;
            }
            switch (currentQuestion.answerType) {
                case AnswerTypeEnum.NPS:
                    if (currentQuestion.minimumValue && Number(answerResponse.response) < Number(currentQuestion.minimumValue)) {
                        setResponseError(t("common:form.ValueGreaterOrEqualThanError", { value: currentQuestion.minimumValue }));
                        isValid = false;
                        return;
                    }
                    if (currentQuestion.maximumValue && Number(currentQuestion.maximumValue) > 0 && Number(answerResponse.response) > Number(currentQuestion.maximumValue)) {
                        setResponseError(t("common:form.ValueSmallerOrEqualThanError", { value: currentQuestion.maximumValue }));
                        isValid = false;
                        return;
                    }
                    break;
                case AnswerTypeEnum.Text:
                    if (currentQuestion.maximumNumberOfCharacters && currentQuestion.maximumNumberOfCharacters > 0 && answerResponse.response!.length > currentQuestion.maximumNumberOfCharacters){
                        setResponseError(t("common:form.MaximumNumberOfCaractersError", { value: currentQuestion.maximumNumberOfCharacters }));
                        isValid = false;
                        return;
                    }
                    break;
                case AnswerTypeEnum.Number:
                    if (currentQuestion.numberOfDecimals != null && currentQuestion.numberOfDecimals >= 0 && answerResponse.response) {
                        const trimmedResponse = answerResponse.response.trim();
                        const separatorIndex = Math.max(trimmedResponse.lastIndexOf('.'), trimmedResponse.lastIndexOf(','));
                        const decimalsCount = separatorIndex >= 0 ? trimmedResponse.length - separatorIndex - 1 : 0;

                        if (decimalsCount > currentQuestion.numberOfDecimals) {
                            setResponseError(t("common:form.MaximumNumberOfDecimalsError", { value: currentQuestion.numberOfDecimals }));
                            isValid = false;
                            return;
                        }
                    }
                    if (currentQuestion.minimumValue && Number(answerResponse.response) < Number(currentQuestion.minimumValue)) {
                        setResponseError(t("common:form.ValueGreaterOrEqualThanError", { value: currentQuestion.minimumValue }));
                        isValid = false;
                        return;
                    }
                    if (currentQuestion.maximumValue && Number(answerResponse.response) > Number(currentQuestion.maximumValue)) {
                        setResponseError(t("common:form.ValueSmallerOrEqualThanError", { value: currentQuestion.maximumValue }));
                        isValid = false;
                        return;
                    }
                    break;
            }
        }

        if (isValid) {
            setResponseError(undefined);
            setPageError(undefined);
            await saveQuestion(answerResponse)
                .then(async (data: SurveyFormulaQuestionResponseDto[]) => {
                    if (data && data.length > 0) {
                        setSurvey((prev) => {
                            if (!prev) return prev;

                            const responseByQuestionId = new Map(
                                data.map((item) => [item.questionId, item.responseValue])
                            );

                            return {
                                ...prev,
                                questions: prev.questions.map((q) => {
                                    const responseValue = responseByQuestionId.get(q.id);
                                    return responseValue === undefined ? q : { ...q, response: responseValue };
                                }),
                            };
                        });
                    }
                    await goToNextQuestion()
                })
                .catch((err) => setPageError(getErrorMessage(err)))
        }
    };

    const goToNextQuestion = async () => {
        if (currentQuestionIndex === orderedQuestions.length - 1) {            
            await setSurveyAsFinish(surveyId!)
                .then(() => setIsCompleted(true))
                .catch((err) => setPageError(getErrorMessage(err)))
        } else {
            setCurrentQuestionIndex((prev) => Math.min(prev + 1, orderedQuestions.length - 1));
            setStartQuestionDate(new Date());
        }
    }

    const changeQuestionValue = (value: string) => {
        if (!currentQuestion) return;
        setResponseError(undefined);

        setSurvey((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                questions: prev.questions.map((q) =>
                    q.id === currentQuestion.id
                        ? { ...q, response: value }
                        : q
                ),
            };
        });
    };

    const changeQuestionOption = (optionId: string) => {
        if (!currentQuestion) return;
        setResponseError(undefined);

        setSurvey((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                questions: prev.questions.map((q) => {
                    if (q.id !== currentQuestion.id) return q;

                    if (q.isMultiselector) {
                        const selectedIds = (q.response ?? "")
                            .split(",")
                            .map((id) => id.trim())
                            .filter(Boolean);

                        const isAlreadySelected = selectedIds.includes(optionId);
                        const nextSelectedIds = isAlreadySelected
                            ? selectedIds.filter((id) => id !== optionId)
                            : [...selectedIds, optionId];

                        return { ...q, response: nextSelectedIds.join(",") };
                    }

                    return { ...q, response: optionId };
                }),
            };
        });
    };

    return (
        <Form>
            <Row>
                <Col xs={6} md={6} className="survey-image-col">
                    <img
                        src="/ilustracio-craiteria-public.png"
                        alt="Craiteria"
                        className="survey-image"
                        style={{ width: '240px', height: 'auto', objectFit: 'cover', backgroundColor: '#FFF' }}
                    />
                </Col>
                <Col xs={6} md={6} className="survey-title">
                    {getLocalizedText(survey?.text)}
                </Col>                
            </Row>
            {isCompleted && (
                <Row>
                    <Col xs={12} className="mt-4 text-center">
                        <h4>{t("common:surveyEndLayoutMessageFinish")}</h4>
                        <p className="mb-0">{t("common:surveyEndLayoutMessageGratitude")}</p>
                    </Col>
                </Row>
            )}
            {!isCompleted && (
                <>
                    <Row>
                        <Col xs={12} className='mt-4'>
                            <SurveyQuestionForm 
                                question={currentQuestion ?? {}}
                                questionIndex={currentQuestionIndex + 1}
                                onChangeQuestionValue={changeQuestionValue}
                                onChangeQuestionOption={changeQuestionOption}
                                errorMessage={responseError}
                            />
                        </Col>
                    </Row>
                    <Row className='mt-2'>
                        {currentQuestion && (
                            <Col xs={12}>                            
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goToPreviousQuestion}
                                        disabled={currentQuestionIndex === 0}
                                        style={{width: "55px"}}
                                    >
                                        <i className="bi bi-chevron-left"></i>
                                    </Button>
                                    <Button
                                        variant="success"
                                        className="survey-answer-button w-100"
                                        onClick={saveQuestionAnswer}
                                    >
                                        {t("survey:answer")}
                                    </Button>
                                </div>
                                {currentQuestion && (
                                    <div className="text-muted mt-2">
                                        {t("survey:questionPagination", { currentPage: currentQuestionIndex + 1, numberOfPages: orderedQuestions.length })}
                                    </div>
                                )}
                            </Col>
                        )}
                        {pageError && (
                            <Col xs={12}>
                                {pageError}
                            </Col>
                        )}
                        {!currentQuestion && !responseError && !pageError && (
                            <Col xs={12}>
                                {t("survey:noAnswers")}
                            </Col>
                        )}
                    </Row>
                </>
            )}
        </Form>
    );
}
